import type { CoreContact } from '@/lib/zifekCore';

interface Props {
  contacts: CoreContact[];
  orgNameById: Record<number, string>;
}

const ROLE_LABELS: Record<string, string> = {
  client: 'Client',
  prospect: 'Prospect',
  supplier: 'Fournisseur',
  partner: 'Partenaire',
  employee: 'Employé',
  professional: 'Professionnel',
  representative: 'Représentant',
  other: 'Autre',
};

export default function ContactsPanel({ contacts, orgNameById }: Props) {
  if (contacts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
        <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
          <i className="ri-contacts-book-2-line text-2xl text-foreground-400"></i>
        </div>
        <p className="text-foreground-600">Aucun contact.</p>
      </div>
    );
  }

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="bg-background-100 text-left text-xs text-foreground-500">
              <th className="px-4 py-3 font-medium">Contact</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium">Organisation</th>
              <th className="px-4 py-3 font-medium">Coordonnées</th>
              <th className="px-4 py-3 font-medium">Statut</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => {
              const name = c.contact_type === 'company'
                ? (c.company_name || '—')
                : [c.first_name, c.last_name].filter(Boolean).join(' ') || '—';
              return (
                <tr key={c.id} className="border-t border-background-200/60">
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground-900 truncate">{name}</div>
                    {c.job_title && <div className="text-xs text-foreground-400 truncate">{c.job_title}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-secondary-100 text-secondary-900 whitespace-nowrap">
                      {c.contact_type === 'company' ? 'Entreprise' : 'Personne'}
                    </span>
                    <span className="ml-1.5 text-[10px] px-2 py-0.5 rounded-full font-medium bg-background-100 text-foreground-600 whitespace-nowrap">
                      {ROLE_LABELS[c.role_type] || c.role_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-foreground-600 truncate">
                    {c.organization_id ? (orgNameById[c.organization_id] || `Org #${c.organization_id}`) : '—'}
                  </td>
                  <td className="px-4 py-3 text-foreground-600">
                    <div className="truncate">{c.email || '—'}</div>
                    <div className="text-xs text-foreground-400">{c.phone || ''}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${c.status === 'active' ? 'bg-accent-100 text-accent-900' : 'bg-foreground-100 text-foreground-600'}`}>
                      {c.status === 'active' ? 'Actif' : c.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}